import { useEffect, useRef } from 'react';

export default function NodesBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let W = 0, H = 0;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    let nodes = [];
    let pulses = [];
    let rafId;
    let resizeTimeout;
    const cfg = { densityFactor: 0.9, lead: '5,150,105', sec: '2,132,199' };

    const motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    let prefersReduced = motionQuery ? motionQuery.matches : false;

    function resize() {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * DPR;
      canvas.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      build();
    }

    function handleResize() {
      // Instantly resize canvas dimensions to avoid stretching
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * DPR;
      canvas.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);

      // Debounce rebuilding nodes to prevent layout/CPU thrashing
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        build();
        draw();
      }, 150);
    }

    function build() {
      const target = Math.round((W * H) / 22000 * cfg.densityFactor);
      nodes = [];
      for (let i = 0; i < target; i++) {
        nodes.push({
          x: Math.random() * W, y: Math.random() * H,
          vx: (Math.random() - 0.5) * 0.22, vy: (Math.random() - 0.5) * 0.22,
          r: Math.random() * 1.6 + 0.6, big: Math.random() < 0.12,
        });
      }
      pulses = [];
    }

    const LINK = 132;

    function spawnPulse() {
      if (nodes.length < 2 || prefersReduced) return;
      const a = (Math.random() * nodes.length) | 0;
      let best = -1, bd = LINK * LINK;
      for (let i = 0; i < nodes.length; i++) {
        if (i === a) continue;
        const dx = nodes[i].x - nodes[a].x, dy = nodes[i].y - nodes[a].y;
        const d = dx * dx + dy * dy;
        if (d < bd && d > 200) { bd = d; best = i; }
      }
      if (best < 0) return;
      pulses.push({ a, b: best, t: 0, sp: 0.012 + Math.random() * 0.02, c: Math.random() < 0.5 ? cfg.lead : cfg.sec });
    }

    let frame = 0;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      frame++;
      
      if (!prefersReduced && frame % 42 === 0 && pulses.length < 7) {
        spawnPulse();
      }

      for (const n of nodes) {
        if (!prefersReduced) {
          n.x += n.vx; n.y += n.vy;
          if (n.x < -20) n.x = W + 20; if (n.x > W + 20) n.x = -20;
          if (n.y < -20) n.y = H + 20; if (n.y > H + 20) n.y = -20;
        }
      }

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < LINK) {
            ctx.strokeStyle = `rgba(16,185,129,${(1 - d / LINK) * 0.10})`;
            ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }

      for (const n of nodes) {
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        if (n.big) {
          ctx.fillStyle = `rgba(${cfg.lead},0.45)`;
          ctx.shadowColor = `rgba(${cfg.lead},0.25)`; ctx.shadowBlur = 4;
        } else {
          ctx.fillStyle = 'rgba(100,116,139,0.22)'; ctx.shadowBlur = 0;
        }
        ctx.fill(); ctx.shadowBlur = 0;
      }

      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        const a = nodes[p.a], b = nodes[p.b];
        if (!a || !b) { pulses.splice(i, 1); continue; }
        
        if (!prefersReduced) {
          p.t += p.sp;
        }
        if (p.t >= 1) { pulses.splice(i, 1); continue; }
        const x = a.x + (b.x - a.x) * p.t, y = a.y + (b.y - a.y) * p.t;
        ctx.beginPath(); ctx.arc(x, y, 2.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.c},0.75)`;
        ctx.shadowColor = `rgba(${p.c},0.4)`; ctx.shadowBlur = 6;
        ctx.fill(); ctx.shadowBlur = 0;
      }

      if (!prefersReduced && document.visibilityState === 'visible') {
        rafId = requestAnimationFrame(draw);
      }
    }

    function handleVisibility() {
      if (document.visibilityState === 'visible' && !prefersReduced) {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(draw);
      } else {
        cancelAnimationFrame(rafId);
      }
    }

    function handleMotionChange(e) {
      prefersReduced = e.matches;
      if (prefersReduced) {
        cancelAnimationFrame(rafId);
        draw(); // draw one static frame
      } else {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(draw);
      }
    }

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibility);
    if (motionQuery) {
      motionQuery.addEventListener('change', handleMotionChange);
    }

    resize();
    if (!prefersReduced) {
      rafId = requestAnimationFrame(draw);
    } else {
      draw(); // Draw static version
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (motionQuery) {
        motionQuery.removeEventListener('change', handleMotionChange);
      }
      clearTimeout(resizeTimeout);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return <canvas ref={canvasRef} id="nodes" />;
}
